import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import FilterListIcon from '@guildeducationinc/recess/icons/FilterListIcon';
import { Popover } from '@mui/material';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useLocale } from '../../hooks';
import { SearchInput } from '../SearchInput';
import { TrackableButton } from '../TrackableButton';
import styles from './SearchFilterBar.module.scss';
/**
 * SearchFilterBar component displays a search input and filter actions.
 *
 * @component
 * @example
 * ```tsx
 * <SearchFilterBar
 *   enableSearch={true}
 *   enableFilter={true}
 *   searchText="example"
 *   searchPlaceholder="Search..."
 *   searchInputStyle={{ width: '200px', border: '1px solid #ccc' }}
 *   onSearchChange={handleSearchChange}
 *   filterActions={filterActions}
 *   isFilterActive={true}
 * />
 * ```
 *
 * @param {boolean} enableSearch - Whether to show the search input.
 * @param {boolean} enableFilter - Whether to show the filter actions.
 * @param {string} searchText - The current search text.
 * @param {string} searchPlaceholder - The placeholder text for the search input.
 * @param {string} searchInputStyle - Custom styles for the search input.
 * @param {Function} onSearchChange - The callback function to handle search input changes.
 * @param {ReactNode} filterActions - The filter actions to display.
 * @param {boolean} isFilterActive - Whether any filter criteria are currently applied.
 * @returns {JSX.Element} The rendered SearchFilterBar component.
 *
 * Notes:
 * - When the Filter popover is open, pressing the Escape key will close it.
 */
export const SearchFilterBar = ({ enableSearch, enableFilter, searchText, searchPlaceholder, searchInputStyle = {}, onSearchChange, filterActions, isFilterActive, }) => {
    const { t } = useLocale();
    const [filterOpen, setFilterOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setFilterOpen(!filterOpen);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setFilterOpen(false);
    };
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };
        if (filterOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        else {
            document.removeEventListener('keydown', handleKeyDown);
        }
        // Cleanup function to remove the event listener when the component unmounts
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [filterOpen]);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    return (_jsxs("div", { id: 'search-filter-bar', className: 'flex gap-2', children: [enableSearch && (_jsx(SearchInput, { onSearchChange: onSearchChange, searchText: searchText, searchPlaceholder: searchPlaceholder, customStyle: searchInputStyle })), enableFilter && (_jsxs("div", { id: 'filter-actions', className: classNames(styles.filterActions), children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Filter', tag: 'search_filter', variant: 'outlined', color: 'secondary', className: styles.filterButton, startIcon: _jsx(FilterListIcon, { size: 24 }), onClick: handleClick, children: t('Filter') }), isFilterActive && _jsx("span", { className: styles.isActive }), filterOpen && (_jsx(Popover, { id: id, open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        }, children: _jsx("div", { id: 'filter-actions-content', className: styles.filterActionsContent, children: filterActions }) }))] }))] }));
};
